import React from 'react';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptions.selectors';
import { connect } from 'react-redux';
import { Specification } from 'gql/autogen/Specification';
import { Box, Notification, TextLink, Typography } from '@drone-js/core';
import {
    getNonExistingKeys,
    isOfferOptionAvailable,
    isSameSpecifications,
    mapOfferOptions,
    TIME_KEY,
} from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptionsUtils';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesOfferOptionClearButtonClickEventActivitiesFieldsV1,
    ActivitiesOfferOptionClearButtonSeenEventActivitiesFieldsV1,
    ActivitiesOfferOptionRadioButtonClickEventActivitiesFieldsV1,
    ActivitiesTimeSlotNotificationSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesOfferOptionClearButtonClickEventActivitiesManifestV1,
    ActivitiesOfferOptionClearButtonSeenEventActivitiesManifestV1,
    ActivitiesOfferOptionRadioButtonClickEventActivitiesManifestV1,
    ActivitiesTimeSlotNotificationSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import TimeSpecification from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/TimeSpecification';
import GenericSpecifications from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/GenericSpecification';
import { killEvent } from 'core/utils/domUtils';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { useLocation } from 'react-router-dom';
import { getLocationWithOutOfferQueryString } from 'core/utils/offerOptionParameters';
import { replaceState } from 'core-framework/window/history';

const NoOfferAvailableNotification: React.FC<{ title: string }> = ({ title }) => {
    return (
        <Box pb="8">
            <Notification
                intent="warning"
                title={title}
                asset={{
                    id: 'fill.symbol.circle-exclamation',
                }}
                radius="m"
            />
        </Box>
    );
};

NoOfferAvailableNotification.displayName = 'NoOfferAvailableNotification';

const OfferOptions: React.FC<Props> = (props) => {
    const {
        selectedOffer,
        selectedOfferId,
        selectedSpecifications,
        setOfferOption,
        setFullOfferOption,
        clearOfferOptions,
        cms,
        possibleOfferOptions,
        allOfferOptions,
        isShowOptionSpecNotification,
        isDisplayWarning,
        setIsDisplayWarning,
        isAvailabilityLoading,
        isSortOfferOptionEnabled,
    } = props;
    const specifications = mapOfferOptions(selectedOffer.offer?.enrichedOfferOption, isSortOfferOptionEnabled);
    const timeSpecification = specifications.find((spec) => spec.key === TIME_KEY);
    const generalSpecification = specifications.filter((spec) => spec.key !== TIME_KEY);
    const isShowTimeSlotNotification = isShowOptionSpecNotification && !selectedSpecifications.some((sl) => sl.key === TIME_KEY);
    const locationSearch = useLocation().search;
    const activityId = selectedOffer.offer?.activityId;

    React.useEffect(() => {
        if (possibleOfferOptions.length === 0 && selectedSpecifications.length > 0) {
            clearOfferOptions(selectedOfferId);
        }
        if (possibleOfferOptions.length === 1) {
            const newSpecifications = possibleOfferOptions[0].specification || [];
            if (!possibleOfferOptions[0]?.isAvailable || isSameSpecifications(newSpecifications, selectedSpecifications)) return;
            setFullOfferOption(selectedOfferId, newSpecifications);
        }
    }, [possibleOfferOptions, selectedOfferId, selectedSpecifications, setFullOfferOption, clearOfferOptions]);

    React.useEffect(() => {
        if (possibleOfferOptions.length === 1) {
            setIsDisplayWarning(false);
        }
    }, [possibleOfferOptions, setIsDisplayWarning]);

    React.useEffect(() => {
        if (timeSpecification && isShowTimeSlotNotification) {
            trackElementEvent<ActivitiesTimeSlotNotificationSeenEventActivitiesFieldsV1>(
                ActivitiesTimeSlotNotificationSeenEventActivitiesManifestV1.ids,
                {},
                { offer_id: selectedOfferId, activity_id: activityId },
            );
        }
    }, [isShowTimeSlotNotification, selectedOfferId, timeSpecification, activityId]);

    React.useEffect(() => {
        trackElementEvent<ActivitiesOfferOptionClearButtonSeenEventActivitiesFieldsV1>(
            ActivitiesOfferOptionClearButtonSeenEventActivitiesManifestV1.ids,
            {},
            { offer_id: selectedOfferId, activity_id: activityId },
        );
    }, [selectedOfferId, activityId]);

    const isSelected = (specification: Specification) => {
        return selectedSpecifications.some((selected) => selected.key === specification.key && selected.specification === specification.specification);
    };

    const onSelected = (specification: Specification, index?: number, isRadio = false) => {
        setOfferOption(selectedOfferId, selectedSpecifications, specification);
        if (isRadio) {
            trackElementEvent<ActivitiesOfferOptionRadioButtonClickEventActivitiesFieldsV1>(
                ActivitiesOfferOptionRadioButtonClickEventActivitiesManifestV1.ids,
                { activities_offer_group: specification.key || '' },
                { offer_id: selectedOfferId, action_element_value: specification.specification, action_element_index: index, activity_id: activityId },
            );
        }
    };

    const onClearOfferOptions = (e: React.MouseEvent) => {
        killEvent(e);
        if (isOn(FeatureName.MaintainSelectedOfferOnDetails)) {
            const queryStringWithoutOfferParams = getLocationWithOutOfferQueryString(locationSearch);
            replaceState(`?${queryStringWithoutOfferParams}`);
        }
        clearOfferOptions(selectedOfferId);
        setIsDisplayWarning(false);
        trackElementEvent<ActivitiesOfferOptionClearButtonClickEventActivitiesFieldsV1>(
            ActivitiesOfferOptionClearButtonClickEventActivitiesManifestV1.ids,
            {},
            { offer_id: selectedOfferId, activity_id: activityId },
        );
    };

    if (specifications.length === 0) {
        if (allOfferOptions.length === 0 && !isAvailabilityLoading) return <NoOfferAvailableNotification title={cms[309268]} />;
        return null;
    }

    return (
        <Box data-testid="offer-options-container" pb="12">
            {allOfferOptions.length === 0 && !isAvailabilityLoading && <NoOfferAvailableNotification title={cms[309268]} />}
            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="start">
                <Box pb="16" data-testid="offer-options-heading">
                    <Typography variant="body-default-strong" tag="h4">
                        {cms[305462]}
                    </Typography>
                    <Typography variant="caption-default">{cms[305463]}</Typography>
                </Box>
                <TextLink
                    data-element-name="activities-offer-option-clear-button"
                    data-testid="activities-offer-option-clear-button"
                    label={cms['264787']}
                    onClick={onClearOfferOptions}
                    textVariant="body-s-strong"
                    leadingIcon="fill.arrow.rotate"
                />
            </Box>
            {isDisplayWarning && !isAvailabilityLoading && (
                <Box pb="16">
                    <Notification
                        intent="warning"
                        title={cms[306207]}
                        asset={{
                            id: 'fill.symbol.circle-exclamation',
                        }}
                        radius="m"
                    />
                </Box>
            )}
            {generalSpecification.map((spec, index) => {
                const nonExistingKeysInSelected = getNonExistingKeys(generalSpecification, selectedSpecifications);
                const isShowOptionRadioNotification = isShowOptionSpecNotification && nonExistingKeysInSelected.includes(spec.key);
                return (
                    <GenericSpecifications
                        key={spec.key}
                        groupIndex={index}
                        cms={cms}
                        mappedSpecKey={spec.key}
                        mappedSpecValues={spec.values}
                        selectedOfferId={selectedOfferId}
                        isShowOptionRadioNotification={isShowOptionRadioNotification}
                        isSelected={isSelected}
                        onSelected={onSelected}
                        isRadioAvailable={(specification) => isOfferOptionAvailable(allOfferOptions, selectedSpecifications, specification)}
                        activityId={activityId}
                    />
                );
            })}
            <TimeSpecification
                timeSpecification={timeSpecification}
                cms={cms}
                isSelected={isSelected}
                isShowTimeSlotNotification={isShowTimeSlotNotification}
                onSelected={onSelected}
                allOfferOptions={allOfferOptions}
                selectedSpecifications={selectedSpecifications}
                selectedOfferId={selectedOfferId}
                activityId={props.selectedOffer.offer?.activityId}
            />
        </Box>
    );
};

OfferOptions.displayName = 'OfferOptions';
export default connect(mapStateToProps, mapDispatchToProps)(OfferOptions);
